import { useMemo } from 'react';
import { Download, FileJson, Link2, Sparkles, Trash2 } from 'lucide-react';
import {
  SEVERITIES,
  type ProfileInput,
  type Report,
  type SectionId,
  type Severity,
} from '../../core/types';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { downloadFile, exportFilename, toJson, toMarkdown } from '../../lib/export';
import { encodeShareLink } from '../../lib/shareLink';
import { SEVERITY_LABEL } from '../../lib/tone';
import { Badge, PassBadge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { useToast } from '../../components/ui/toastContext';
import { FindingCard } from './FindingCard';
import { KeywordPanel } from './KeywordPanel';
import { RewritePanel } from './RewritePanel';
import { ScoreRing } from './ScoreRing';
import { SectionBar } from './SectionBar';

export interface ReportPanelProps {
  report: Report;
  profile: ProfileInput;
  /** Scrolls the matching form section into view. */
  onJumpToSection: (section: SectionId) => void;
  onClearData: () => void;
}

export function ReportPanel({ report, profile, onJumpToSection, onClearData }: ReportPanelProps) {
  const toast = useToast();
  const { copy } = useCopyToClipboard();

  const bySeverity = useMemo(() => {
    const groups = new Map<Severity, Report['findings']>();
    for (const severity of SEVERITIES) groups.set(severity, []);
    for (const finding of report.findings) groups.get(finding.severity)?.push(finding);
    return groups;
  }, [report.findings]);

  const onCopyShareLink = async () => {
    const url = encodeShareLink({ profile, mode: report.mode }, window.location.href);
    const ok = await copy(url, 'share');
    if (ok) toast.success('Link copied. Anyone with it can see this profile draft.');
    else toast.error('Could not copy the link — your browser blocked clipboard access');
  };

  return (
    <div className="space-y-5">
      {/* ----------------------------------------------------------- score --- */}
      <Card elevated>
        <CardBody className="space-y-6">
          <ScoreRing score={report.overall} grade={report.grade} />

          <div className="space-y-1">
            {report.sections.map((section) => (
              <SectionBar key={section.section} section={section} onSelect={onJumpToSection} />
            ))}
          </div>

          <div className="no-print flex flex-wrap gap-2 border-t border-line pt-4">
            <Button
              size="sm"
              variant="secondary"
              icon={<Download />}
              onClick={() =>
                downloadFile(exportFilename(report, 'md'), toMarkdown(report), 'text/markdown')
              }
            >
              Markdown
            </Button>
            <Button
              size="sm"
              variant="secondary"
              icon={<FileJson />}
              onClick={() =>
                downloadFile(
                  exportFilename(report, 'json'),
                  toJson(report, profile),
                  'application/json'
                )
              }
            >
              JSON
            </Button>
            <Button size="sm" variant="secondary" icon={<Link2 />} onClick={onCopyShareLink}>
              Copy share link
            </Button>
            <Button size="sm" variant="danger" icon={<Trash2 />} onClick={onClearData}>
              Clear my data
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* ------------------------------------------------------ top fixes --- */}
      {report.topFixes.length > 0 ? (
        <Card>
          <CardBody className="space-y-3">
            <CardHeader
              as="h3"
              title="Start here"
              description="The three changes that move your score most. Everything else can wait."
              aside={
                <span aria-hidden="true" className="text-brand-500">
                  <Sparkles className="size-5" />
                </span>
              }
            />
            <ol className="space-y-2">
              {report.topFixes.map((finding, index) => (
                <li key={finding.id} className="flex gap-3">
                  {/* The rank is real content, so the digit owes 4.5:1. White on
                      brand-500 is only 4.23:1, so dark mode inverts to a bright
                      chip with a near-black digit (5.61:1) rather than dimming
                      it — which also happens to look better on near-black. */}
                  <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-pill bg-brand-600 text-xs font-semibold text-white dark:bg-brand-400 dark:text-brand-950">
                    {index + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-strong">{finding.title}</p>
                    <p className="mt-0.5 text-sm text-muted">{finding.fix}</p>
                  </div>
                </li>
              ))}
            </ol>
          </CardBody>
        </Card>
      ) : null}

      {/* ------------------------------------------------------- keywords --- */}
      <KeywordPanel keywords={report.keywords} />

      {/* ------------------------------------------------------- findings --- */}
      <Card>
        <CardBody className="space-y-4">
          <CardHeader
            as="h3"
            title="Everything we found"
            description="Grouped worst first. Each one explains why it matters, not just what to change."
            aside={
              report.findings.length === 0 ? (
                <PassBadge />
              ) : (
                <Badge tone="neutral">{report.findings.length} total</Badge>
              )
            }
          />

          {report.findings.length === 0 ? (
            <p className="rounded-field border border-success-200 bg-success-50 p-4 text-sm text-success-700 dark:border-success-600 dark:bg-success-950 dark:text-success-100">
              Every check passed. Worth re-running this after your next role or project — the advice
              shifts as your profile grows.
            </p>
          ) : null}

          {SEVERITIES.map((severity) => {
            const findings = bySeverity.get(severity) ?? [];
            if (findings.length === 0) return null;
            return (
              <section key={severity} className="space-y-2">
                <h4 className="flex items-center gap-2 text-xs font-semibold tracking-wide text-subtle uppercase">
                  {SEVERITY_LABEL[severity]}
                  <span className="rounded-pill bg-sunken px-1.5 py-0.5 font-mono text-[0.7rem] font-normal">
                    {findings.length}
                  </span>
                </h4>
                <div className="space-y-2">
                  {findings.map((finding) => (
                    <FindingCard
                      key={finding.id}
                      finding={finding}
                      // Critical findings open by default: if something is
                      // actively costing the user opportunities, they should not
                      // have to click to find out what.
                      defaultOpen={severity === 'critical'}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </CardBody>
      </Card>

      {/* ------------------------------------------------------- rewrites --- */}
      <RewritePanel rewrites={report.rewrites} />
    </div>
  );
}
